import { useLayoutEffect, useState } from 'react';

import { ColumnSizingState, Table } from '@tanstack/react-table';

import { getVisibleWidth } from '@utils/browser';

import { AvailableColumn, ColumnType, DataItemType } from './types';

export const selectionColumnWidth = 40;
export const spacerColumnWidth = 16;
export const fixedColumnWidth = 48;
export const minimumColumnSize = 120;
export const tableActionPrefix = 'table-action';

// There is no whitepaper explaining these default values.
// I just changed them around a bit until I liked them. -siggirh
let desiredWidthBase = 200;
const browserWidth = getVisibleWidth();
if (browserWidth > 1600) {
  desiredWidthBase = 250;
}

export const defaultSizingMap = {
  boolean: minimumColumnSize,
  custom: desiredWidthBase,
  date: desiredWidthBase,
  datetime: desiredWidthBase,
  email: desiredWidthBase,
  file: desiredWidthBase * 0.7,
  file_multi: desiredWidthBase * 0.8,
  multi_select: desiredWidthBase,
  paragraph: desiredWidthBase * 1.2,
  phone_number: desiredWidthBase * 0.7,
  progress: desiredWidthBase * 0.8,
  single_select: desiredWidthBase,
  text: desiredWidthBase,
  user: desiredWidthBase,
  number: desiredWidthBase * 0.7,
  decimal: desiredWidthBase * 0.7,
  issues: desiredWidthBase * 0.1,
  array: desiredWidthBase * 0.8,
  array_of_objects: desiredWidthBase * 0.8,
} satisfies Record<ColumnType, number>;

export const useColumnSizingVars = <T extends DataItemType = DataItemType>(
  table: Table<any>,
  columnSizing: ColumnSizingState,
  columns: Array<AvailableColumn<T>>
) => {
  const [isInitialized, setIsInitialized] = useState(false);

  useLayoutEffect(() => {
    if (isInitialized) return;

    requestAnimationFrame(() => {
      const newSizing = {} as Record<keyof DataItemType, number>;
      columns.forEach((column) => {
        if (!column.type) {
          newSizing[column.id] = columnSizing?.[column.id] || desiredWidthBase;
        } else {
          const width = Math.max(defaultSizingMap[column.type], minimumColumnSize);
          newSizing[column.id] = columnSizing?.[column.id] || width;
        }
      });

      table.setColumnSizing(newSizing);
      setIsInitialized(true);
    });
  }, [isInitialized, columnSizing, table, columns]);

  const variables: Record<string, number> = {};
  if (columnSizing) {
    variables[tableSizeVariableName] = table.getTotalSize();

    Object.entries(columnSizing).forEach(([key, value]) => {
      variables[getHeaderSizeVariableName(key)] = value;
    });
  }

  return {
    isInitialized,
    sizingVariables: getColumnSizingVariables(table),
  };
};

export const getIsTableAction = (columnId: string) => {
  return columnId.startsWith(tableActionPrefix);
};

export const getSizeVariableName = (prefix: string, id?: string) => {
  const idString = id ? `${id}-` : '';
  return `--${prefix}-${idString}size`;
};

export const getHeaderSizeVariableName = (id: string) => {
  return getSizeVariableName('header', id);
};

export const tableSizeVariableName = getSizeVariableName('table');

export const getVariableSize = (id: string) =>
  `max(calc(var(${id}) * 1px), ${minimumColumnSize}px)`;

export const getColumnWidth = (columnId: string) => {
  if (columnId === 'table-action-select') {
    return selectionColumnWidth;
  }
  return getIsTableAction(columnId)
    ? `${fixedColumnWidth}px`
    : getVariableSize(getHeaderSizeVariableName(columnId));
};

export const getColumnSizingVariables = (table: Table<any>) => {
  const variables: Record<string, number> = {};
  const viewColumnSizing = table.getState().columnSizing;
  if (viewColumnSizing) {
    variables[tableSizeVariableName] = table.getTotalSize();

    Object.entries(viewColumnSizing).forEach(([key, value]) => {
      variables[getHeaderSizeVariableName(key)] = value;
    });
  }

  return variables;
};
